/*@flow*/
import * as t from 'lib/t';
import { Id, NormalizedNumber } from 'types/tcomb';
import enumsFor from './util/enumsFor';
import mkTagTemplate from 'lib/templates/mkTagTemplate';
import Hours from './types/Hours';
import isEmbedded from 'lib/isEmbedded';

const config = {
  id: t.maybe(Id).options({ type: 'hidden' }),
  propertyId: t.maybe(Id).options({ type: 'hidden' }),
  apn: t.maybe(Id).options({ type: 'hidden' }),
  name: t.String.opt({
    attrs: {
      autoComplete: 'off',
    },
  }),
  address: t.maybe(t.String).options({
    template: 'LocationPickerTemplate',
  }),
  theGeom: t.String.options({ type: 'hidden' }),
  mapLabel: t.maybe(t.String),
  visibility: enumsFor('visibility'),
  category: t.String.options({
    template: 'TenantCategory',
    attrs: {
      placeholder: 'Select...',
      classNamePrefix: 'Select',
    },
  }),
  subcategories: t.maybe(t.list(t.String)).options({
    template: 'TenantSubcategory',
    attrs: {
      placeholder: 'Select...',
      classNamePrefix: 'Select',
    },
  }),
  tags: t.maybe(t.list(t.String)).options({
    template: mkTagTemplate('tenants'),
    attrs: {
      placeholder: 'Select...',
      classNamePrefix: 'Select',
    },
  }),
  status: t.String.options({
    template: 'TenantStatus',
    hideWhen: isEmbedded,
    attrs: {
      placeholder: 'Choose status of tenant',
      classNamePrefix: 'Select',
    },
  }),
  description: t.maybe(t.String).options({
    template: 'TextEditorTemplate',
    type: 'textarea',
  }),
  photoUrls: t.maybe(t.list(t.String)).options({
    label: 'Photo(s)',
    template: 'PhotoUploaderTemplate',
    help: 'Photos must be larger than 600px wide.',
  }),
  unitName: t.maybe(t.String),
  use: t.maybe(enumsFor('use')),
  rentableSqft: t.maybe(NormalizedNumber),
  numEmployees: t.maybe(NormalizedNumber).options({
    label: 'Number of Employees',
  }),
  numFullTimeEmployees: t.maybe(NormalizedNumber).options({
    label: 'Number of Fulltime Employees',
  }),
  numPartTimeEmployees: t.maybe(NormalizedNumber).options({
    label: 'Number of Part-Time Employees',
  }),
  businessOpenedOn: t.maybe(t.Date).options({
    label: 'Date opened',
  }),
  businessClosedOn: t.maybe(t.Date).options({
    label: 'Date closed',
  }),
  leaseStart: t.maybe(t.Date),
  leaseExpiration: t.maybe(t.Date),
  askingPricePerSqft: t.maybe(NormalizedNumber),
  contractValuePerSqft: t.maybe(NormalizedNumber),
  url: t.maybe(t.String).options({
    label: 'Website',
  }),
  hours: Hours.options({
    template: 'AddHoursTemplate',
  }),
  phone: t.maybe(t.String),
  fax: t.maybe(t.String),
  email: t.maybe(t.String),
  notes: t
    .maybe(t.String)
    .options({ type: 'textarea', template: 'TextEditorTemplate' }),
  perks: t.maybe(t.String).options({
    label: 'Perks and Special Offers',
    type: 'textarea',
  }),
  facebookLink: t.maybe(t.String).options({
    label: 'Facebook',
    type: 'url',
    before: {
      tag: 'h3',
      props: { children: 'Social Media', className: 'mt-5' },
    },
  }),
  instagramLink: t.maybe(t.String).options({
    label: 'Instagram',
    type: 'url',
  }),
  twitterLink: t.maybe(t.String).options({
    label: 'Twitter',
    type: 'url',
  }),
  youtubeLink: t.maybe(t.String).options({
    label: 'Youtube',
    type: 'url',
  }),
  pinterestLink: t.maybe(t.String).options({
    label: 'Pinterest',
    attrs: {
      type: 'url',
    },
  }),
  yelpLink: t.maybe(t.String).options({
    label: 'Yelp',
    attrs: {
      type: 'url',
    },
  }),

  contactIds: t.maybe(t.list(Id)).opt({
    before: {
      tag: 'h3',
      props: { children: 'Associated Records', className: 'mt-5' },
    },
    after: {
      tag: 'div',
      props: { className: 'mb-5 mt-5' },
    },
    template: 'Contacts',
    label: 'Contacts',
    attrs: {
      placeholder: 'Select Contact',
      classNamePrefix: 'SelectRecordContacts',
    },
    hideWhen: isEmbedded,
  }),
  buildingId: t.maybe(Id).opt({
    template: 'TenantBuilding',
    label: 'Building',
    attrs: {
      classNamePrefix: 'SelectNearbyBuildings',
    },
  }),
  unitIds: t.maybe(t.list(Id)).opt({
    template: 'TenantUnits',
    label: 'Units',
    attrs: {
      classNamePrefix: 'SelectNearbyUnits',
    },
  }),
  primaryContactName: t.maybe(t.String).opt({
    before: {
      tag: 'h3',
      props: { children: 'Primary Contact', className: 'mt-5' },
    },
    label: 'Name',
    hideWhen: isEmbedded,
  }),
  primaryContactPhone: t
    .maybe(t.String)
    .opt({ label: 'Phone', hideWhen: isEmbedded }),
  primaryContactEmail: t
    .maybe(t.String)
    .opt({ label: 'Email', hideWhen: isEmbedded }),
  secondaryContactName: t.maybe(t.String).opt({
    before: {
      tag: 'h3',
      props: { children: 'Secondary Contact', className: 'mt-5' },
    },
    label: 'Name',
    hideWhen: isEmbedded,
  }),
  secondaryContactPhone: t
    .maybe(t.String)
    .opt({ label: 'Phone', hideWhen: isEmbedded }),
  secondaryContactEmail: t
    .maybe(t.String)
    .opt({ label: 'Email', hideWhen: isEmbedded }),
};

export default t.struct(config, 'Tenant').opt<any>({ legend: '' });
